import { ReactElement, useCallback } from 'react';
import { useParams } from 'react-router';
import { Col } from 'reactstrap';
import { createFetchContext } from '../../../../../../components/contexts/FetchContextFactory';
import DisplayError from '../../../../../../components/info/DisplayError';
import Loading from '../../../../../../components/info/Loading';
import { getOtherStudies } from '../../../../api/educationalInformationRequests';
import {
  OtherStudies,
  OtherStudiesLoaderQuery,
  OtherStudiesPagination,
  OtherStudy,
} from '../../types';

export interface OtherStudiesLoaderProps {
  children: (props: {
    otherStudies: OtherStudy[];
    otherStudiesPagination: OtherStudiesPagination;
    query: OtherStudiesLoaderQuery;
    updateQuery: (query: OtherStudiesLoaderQuery) => void;
    refresh: () => Promise<void>;
  }) => ReactElement;
}

const { FetchProvider, FetchConsumer, useFetch } = createFetchContext<
  OtherStudiesLoaderQuery,
  OtherStudies
>();

export function useOtherStudies() {
  return useFetch();
}

export const OtherStudiesLoaderConsumer = FetchConsumer;

export const ITEMS_PER_PAGE_DEFAULT = 10;
export const PAGE_INITIAL_DEFAULT = 1;

export function OtherStudiesLoader({ children }: OtherStudiesLoaderProps) {
  const { studentId } = useParams<{ studentId: string }>();
  const request = useCallback(
    async (query: OtherStudiesLoaderQuery) => {
      return getOtherStudies(studentId, query.items, query.page);
    },
    [studentId],
  );

  return (
    <FetchProvider
      request={request}
      defaultQuery={{
        items: ITEMS_PER_PAGE_DEFAULT,
        page: PAGE_INITIAL_DEFAULT,
      }}
      fetchImmediately={true}
    >
      <FetchConsumer>
        {({ data, loading, error, refresh, query, updateQuery }) => {
          if (error) {
            return (
              <Col md={12}>
                <DisplayError
                  insideCard
                  textBody={error.code}
                  retryAction={refresh}
                  loadingAction={loading}
                />
              </Col>
            );
          }
          if (loading) {
            return (
              <Col md={12}>
                <Loading insideCard />
              </Col>
            );
          }

          if (!data) {
            return (
              <DisplayError
                insideCard
                textBody="Data no cargada"
                retryAction={refresh}
                loadingAction={loading}
              />
            );
          }
          return children({
            otherStudies: data.otherStudies,
            otherStudiesPagination: data.otherStudiesPagination,
            query,
            updateQuery,
            refresh,
          });
        }}
      </FetchConsumer>
    </FetchProvider>
  );
}
